import {nanoid} from 'nanoid/non-secure';
import {
  type GaugeEvent,
  INCREMENT_EVENTS,
  type IncrementEvent,
  type MetricParams,
  type MetricTags,
  type Metrics,
  type SetEvent,
} from 'src/metrics';
import {WILDCARD_ORIGIN} from 'src/origin';
import {parseUrl} from 'src/utils/url';

import type {Logger} from 'src/logger';
import type {Target} from 'src/target';
import type {Message, TargetContext} from 'src/target/context';

/**
 * The base options for configuring a messenger
 */
export interface BaseMessengerOptions {
  /**
   * The logger to use for logging messages from this messenger.
   */
  logger: Logger;

  /**
   * The metrics client to use for recording metrics from this messenger.
   */
  metrics: Metrics;

  /**
   * The target that this messenger is running in
   */
  self: Target;

  /**
   * The target that this messenger is communicating with
   */
  remote: Target;

  /**
   * Whether to allow wildcard origins for the remote target.
   *
   * NOTE: **This is NOT recommended.**
   */
  UNSAFE_allowWildcardOrigin?: boolean;
}

/**
 * The base messenger abstract class that provides common functionality for all messengers.
 */
export abstract class BaseMessenger {
  // we need to keep track of the listener since we wrap it to do target verification
  private listenerMap = new WeakMap<
    (message: Message<any>) => void,
    (message: Message<any>) => void
  >();

  protected id = nanoid();

  /**
   * An abort controller used to abort any ongoing calls when the messenger is destroyed.
   * This is useful to prevent memory leaks and ensure that no further messages are processed
   * after the messenger is destroyed.
   */
  protected controller = new AbortController();

  protected abstract messengerType: string;

  constructor(private _options: BaseMessengerOptions) {
    // validate remote origin
    if (
      this._options.remote.origin === WILDCARD_ORIGIN &&
      !this._options.UNSAFE_allowWildcardOrigin
    ) {
      throw new Error('Wildcard origins are not allowed');
    }
    if (
      this._options.remote.origin &&
      this._options.remote.origin !== WILDCARD_ORIGIN &&
      !parseUrl(this._options.remote.origin)
    ) {
      throw new Error(
        `Invalid origin. Make sure it is a fully qualified URL e.g. https://example.com`,
      );
    }
    this.debug('INIT messenger', this.id);
  }

  /**
   * Send a message to the remote target.
   * This method directly calls `postMessage` on the remote target context with the provided data.
   */
  protected sendRawMessage<T = unknown>(
    data: T,
    transfer?: Transferable[],
  ): void {
    const targetOrigin = this.remoteOrigin();
    this.debug('--> SEND message:', this.id, targetOrigin);
    this.remoteContext().postMessage(data, {
      targetOrigin,
      transfer,
    });
    this.increment(INCREMENT_EVENTS.SEND_MESSAGE);
  }

  /**
   * Add a message event listener to the self target context.
   * This method wraps the provided listener to verify the origin and source of the event before calling the listener.
   */
  protected addMessageEventListener<T = unknown>(
    listener: (message: Message<T>) => void,
    opts?: {additionalValidation?: (message: Message<T>) => boolean},
  ): void {
    const wrappedListener = (message: Message<T>): void => {
      if (
        !this.selfContext().fromValidContext(message, {
          allowedOrigin: this.remoteOrigin(),
          allowedSource: this.remoteContext().ref,
        })
      ) {
        this.debug(
          'Received message from invalid context',
          `Origin: "${message.origin}"`,
          message.source ? 'has source' : 'no source',
        );
        this.increment(INCREMENT_EVENTS.TARGET_VERIFICATION_FAILED);
        return;
      }

      if (opts?.additionalValidation && !opts.additionalValidation(message)) {
        this.error('Received message failed additional validation');
        return;
      }
      this.debug('<-- RECV message:', this.id);

      listener(message);
    };

    this.selfContext().addMessageEventListener(wrappedListener, {
      signal: this.controller.signal,
    });
    this.listenerMap.set(listener, wrappedListener);
  }

  /**
   * Remove a previously added message event listener from the self target context.
   */
  protected removeMessageEventListener<T = unknown>(
    listener: (message: Message<T>) => void,
  ): void {
    const wrappedListener = this.listenerMap.get(listener);
    if (wrappedListener) {
      this.selfContext().removeMessageEventListener(wrappedListener);
      this.listenerMap.delete(listener);
    }
  }

  /**
   * Destroy the messenger and abort any ongoing calls.
   */
  destroy(): void {
    this.controller.abort('Messenger has been destroyed.');
  }

  /**
   * Log a debug message.
   */
  protected debug(message: string, ...args: unknown[]): void {
    this._options.logger.debug(`[${this.id}]`, message, ...args);
  }

  /**
   * Log a warn message.
   */
  protected warn(message: string, ...args: unknown[]): void {
    this._options.logger.warn(`[${this.id}]`, message, ...args);
  }

  /**
   * Log an error message.
   */
  protected error(message: string, ...args: unknown[]): void {
    this._options.logger.error(`[${this.id}]`, message, ...args);
  }

  /**
   * Increment a metric.
   */
  protected increment(event: IncrementEvent, tags?: MetricTags): void {
    this._options.metrics.increment(event, {
      ...tags,
      messenger_type: this.messengerType,
    });
  }

  protected gauge(
    event: GaugeEvent,
    duration: number,
    tags?: MetricTags,
  ): void {
    this._options.metrics.gauge(event, duration, {
      ...tags,
      messenger_type: this.messengerType,
    });
  }

  protected set(
    event: SetEvent,
    params: MetricParams,
    tags?: MetricTags,
  ): void {
    this._options.metrics.set(event, params, {
      ...tags,
      messenger_type: this.messengerType,
    });
  }

  protected generateId(): string {
    this.debug('generateId');
    return nanoid();
  }

  private remoteOrigin(): string {
    return this._options.remote.origin ?? location.origin;
  }

  protected remoteContext(): TargetContext {
    return this._options.remote.context;
  }

  protected selfContext(): TargetContext {
    return this._options.self.context;
  }
}
